import {
  Button,
  Col,
  Divider,
  Flex,
  message,
  notification,
  Row,
  Table,
  TableColumnType,
  Typography,
} from "antd";
import { PlusCircleOutlined } from "@ant-design/icons";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { ServiceItem } from "api/requests";
import {
  useInventoryServiceDeleteService,
  useInventoryServiceGetServices,
  useInventoryServiceGetServicesKey,
} from "api/queries";
import queryString from "query-string";
import { CreateServiceModal } from "./CreateServiceModal";
import { useAbility } from "@casl/react";
import { AbilityContext } from "utils/abilit";
import { PermissionType, SubjectKey } from "utils/enums";
import { TableActions } from "components/common";
import UpdateServiceModal from "./UpdateServiceModal";
import { useQueryClient } from "@tanstack/react-query";

export const ServiceListingPage = () => {
  const [createServiceModalOpen, setCreateServiceModalOpen] = useState(false);
  const [updateServiceModalOpen, setUpdateServiceModalOpen] = useState(false);
  const [selectedService, setSelectedService] = useState<any>(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const ability = useAbility(AbilityContext);
  const queryClient = useQueryClient();

  const { data, isLoading, isPending, isFetching, refetch, isRefetching } =
    useInventoryServiceGetServices(
      {
        filters: "",
        pagination: queryString.stringify({
          offset: (page - 1) * limit,
          limit,
        }),
        sort: "",
      },
      [useInventoryServiceGetServicesKey]
    );

  const { mutate: deleteService } = useInventoryServiceDeleteService();

  useEffect(() => {
    refetch();
  }, [page, limit]);

  const handleOpenUpdateServiceModal = (service: any) => {
    setUpdateServiceModalOpen(true);
    setSelectedService(service);
  };

  const handleCloseUpdateServiceModal = () => {
    setUpdateServiceModalOpen(false);
    setSelectedService(null);
  };

  const handleDeleteService = async (service: any) => {
    deleteService(
      { idService: service.idService },
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries({
            queryKey: [useInventoryServiceGetServicesKey],
            refetchType: "all",
          });

          message.success("Service deleted successfully");
        },
        onError: (error: any) => {
          notification.error({
            message: "Failed to delete service",
            description:
              error?.body?.message ||
              error?.response?.data?.message ||
              error?.message ||
              "An unexpected error occurred.",
          });
        },
      }
    );
  };

  const columns: TableColumnType<ServiceItem>[] = [
    {
      key: "serviceName",
      title: "Name",
      dataIndex: "serviceName",
    },
    {
      key: "description",
      title: "Description",
      dataIndex: "description",
    },
    {
      key: "serviceCategory",
      title: "Service Category",
      dataIndex: "serviceCategory",
      render: (value) => value.serviceCategoryName,
    },
    {
      key: "isActive",
      title: "Is Active",
      dataIndex: "isActive",
      render: (value) => (value ? "Yes" : "No"),
    },
    {
      key: "actions",
      title: "Actions",
      dataIndex: "actions",
      render: (_, record) => (
        <TableActions
          onEdit={
            ability.can(PermissionType.UPDATE, SubjectKey.SERVICE)
              ? () => handleOpenUpdateServiceModal(record)
              : undefined
          }
          onDeletePopConfirm={
            ability.can(PermissionType.DELETE, SubjectKey.SERVICE)
              ? () => handleDeleteService(record)
              : undefined
          }
          onDeletePopConfirmMessage="Are you sure?"
          onDeletePopConfirmDescription="This service will be deleted permanently"
        />
      ),
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1.5, delay: 0 }}
    >
      <Row>
        <Col xs={12}>
          <Flex justify="start" align="center">
            <Typography.Title level={2} style={{ margin: 0 }}>
              Services
            </Typography.Title>
          </Flex>
        </Col>
        <Col xs={12}>
          <Flex justify="end" align="center">
            <Button
              icon={<PlusCircleOutlined />}
              type="primary"
              onClick={() => setCreateServiceModalOpen(true)}
              disabled={!ability.can(PermissionType.CREATE, SubjectKey.SERVICE)}
            >
              Create
            </Button>
          </Flex>
        </Col>

        <Col xs={24}>
          <Divider style={{ opacity: 0.4 }} />
        </Col>

        <Col xs={24}>
          <Table
            columns={columns}
            dataSource={data?.items}
            loading={isLoading || isPending || isFetching || isRefetching}
            key={"service-list"}
            rowKey={(record) => record.idService}
            pagination={{
              current: page,
              pageSize: limit,
              total: data?.total,
              onChange: (page) => setPage(page),
              onShowSizeChange: (_, limit) => setLimit(limit),
              pageSizeOptions: [10, 20, 50, 100],
              showSizeChanger: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} items`,
            }}
          />
        </Col>

        <CreateServiceModal
          isOpen={createServiceModalOpen}
          onClose={() => setCreateServiceModalOpen(false)}
        />

        <UpdateServiceModal
          service={selectedService}
          isOpen={updateServiceModalOpen}
          onClose={handleCloseUpdateServiceModal}
        />
      </Row>
    </motion.div>
  );
};

export default ServiceListingPage;
